import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { useRouter } from '../hooks/useRouter';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/oV66CvE5T9DxmmC6cXmgrm/ChatGPT_Image_Mar_27__2026__07_43_24_PM-removebg-preview.png';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [careersOpen, setCareersOpen] = useState(false);
  const {
    currentRoute,
    navigate
  } = useRouter();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };
  const isActive = (path: string) => {
    if (path === '/' && (currentRoute === '/' || currentRoute === '')) return true;
    if (path !== '/' && currentRoute.startsWith(path)) return true;
    return false;
  };
  const isAboutActive = isActive('/about') || isActive('/testimonials') || isActive('/faq');
  const isCareersActive = isActive('/employment');
  const services = [{
    label: 'Respite & Holiday Care',
    path: '/services/respite-care'
  }, {
    label: 'Planning & Funding Care',
    path: '/services/planning-funding'
  }, {
    label: 'Concierge Services',
    path: '/services/concierge'
  }, {
    label: 'In-home Care Services',
    path: '/services/in-home-care'
  }, {
    label: 'Foley Care',
    path: '/services'
  }, {
    label: 'G-Tube',
    path: '/services'
  }, {
    label: 'Medication Reminders',
    path: '/services'
  }, {
    label: 'Occupational Therapy',
    path: '/services'
  }, {
    label: 'Personal Care Services',
    path: '/services'
  }, {
    label: 'Physical Therapy',
    path: '/services'
  }, {
    label: 'Tracheostomy',
    path: '/services'
  }, {
    label: 'Wound Care',
    path: '/services'
  }];
  const aboutItems = [{
    label: 'About Us',
    path: '/about'
  }, {
    label: 'Testimonials',
    path: '/testimonials'
  }, {
    label: 'FAQs',
    path: '/faq'
  }];
  const careersItems = [{
    label: 'Employment Application',
    path: '/employment'
  }, {
    label: 'Application Form',
    path: '/application-form'
  }];
  const navLinkClass = (path: string) => `whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${isActive(path) ? 'text-primary' : 'text-dark-muted hover:text-primary'}`;
  const dropdownBtnClass = (active: boolean) => `whitespace-nowrap flex items-center px-3 py-2 text-sm font-medium transition-colors ${active ? 'text-primary' : 'text-dark-muted hover:text-primary'}`;
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 backdrop-blur-sm py-3'}`} data-id="element-209">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-210">
        <div className="flex justify-between items-center" data-id="element-211">
          {/* Logo */}
          <div className="flex items-center cursor-pointer group flex-shrink-0" onClick={() => handleNavClick('/')} data-id="element-212">
            <img src={LOGO_URL} alt="Edvin Home Healthcare Logo" className="h-14 w-14 object-contain mr-3" data-id="element-213" />
            <div className="flex flex-col whitespace-nowrap leading-tight" data-id="element-214">
              <span className="text-lg xl:text-xl font-bold text-primary-dark tracking-widest uppercase" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-215">
                Edvin Home
              </span>
              <span className="text-[10px] xl:text-xs font-semibold text-primary tracking-[0.25em] uppercase" data-id="element-216">
                Healthcare Solutions
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-id="element-217">
            <button onClick={() => handleNavClick('/')} className={navLinkClass('/')} data-id="element-218">
              Home
            </button>

            {/* About Dropdown (About Us, Testimonials, FAQs) */}
            <div className="relative group" data-id="element-219">
              <button onClick={() => handleNavClick('/about')} className={dropdownBtnClass(isAboutActive)} data-id="element-220">
                About
                <ChevronDownIcon className="ml-1 h-3.5 w-3.5 group-hover:rotate-180 transition-transform duration-200" data-id="element-221" />
              </button>
              <div className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-222">
                <div className="py-2" data-id="element-223">
                  {aboutItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${isActive(item.path) ? 'text-primary bg-primary/5 font-semibold' : 'text-dark-muted hover:bg-primary/5 hover:text-primary'}`} data-id="element-224">
                      {item.label}
                    </button>)}
                </div>
              </div>
            </div>

            {/* Services Dropdown */}
            <div className="relative group" data-id="element-225">
              <button onClick={() => handleNavClick('/services')} className={dropdownBtnClass(isActive('/services'))} data-id="element-226">
                Services
                <ChevronDownIcon className="ml-1 h-3.5 w-3.5 group-hover:rotate-180 transition-transform duration-200" data-id="element-227" />
              </button>
              <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-228">
                <div className="py-2 grid grid-cols-1 max-h-96 overflow-y-auto" data-id="element-229">
                  {services.map((service, idx) => <button key={idx} onClick={() => handleNavClick(service.path)} className="text-left px-4 py-2.5 text-sm text-dark-muted hover:bg-primary/5 hover:text-primary transition-colors" data-id="element-230">
                      {service.label}
                    </button>)}
                </div>
              </div>
            </div>

            <button onClick={() => handleNavClick('/resources')} className={navLinkClass('/resources')} data-id="element-231">
              Resources
            </button>

            {/* Careers Dropdown */}
            <div className="relative group" data-id="element-232">
              <button onClick={() => handleNavClick('/employment')} className={dropdownBtnClass(isCareersActive)} data-id="element-233">
                Careers
                <ChevronDownIcon className="ml-1 h-3.5 w-3.5 group-hover:rotate-180 transition-transform duration-200" data-id="element-234" />
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-235">
                <div className="py-2" data-id="element-236">
                  {careersItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className="block w-full text-left px-4 py-2.5 text-sm text-dark-muted hover:bg-primary/5 hover:text-primary transition-colors" data-id="element-237">
                      {item.label}
                    </button>)}
                </div>
              </div>
            </div>

            <button onClick={() => handleNavClick('/contact')} className="ml-4 whitespace-nowrap bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md" data-id="element-238">
              Contact Us
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center" data-id="element-239">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-dark hover:text-primary p-2 focus:outline-none" aria-label="Toggle mobile menu" data-id="element-240">
              {mobileMenuOpen ? <XIcon className="h-7 w-7" data-id="element-241" /> : <MenuIcon className="h-7 w-7" data-id="element-242" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`lg:hidden fixed inset-0 top-[76px] bg-white z-40 overflow-y-auto transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-243">
        <div className="flex flex-col px-6 py-8 space-y-4" data-id="element-244">
          <button onClick={() => handleNavClick('/')} className={`text-left text-lg font-medium py-2 border-b border-gray-100 ${isActive('/') ? 'text-primary' : 'text-dark'}`} data-id="element-245">
            Home
          </button>

          {/* About Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-246">
            <button onClick={() => setAboutOpen(!aboutOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isAboutActive ? 'text-primary' : 'text-dark'}`} data-id="element-247">
              About
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${aboutOpen ? 'rotate-180' : ''}`} data-id="element-248" />
            </button>
            {aboutOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-249">
                {aboutItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className={`text-left ${isActive(item.path) ? 'text-primary font-semibold' : 'text-dark-muted'}`} data-id="element-250">
                    {item.label}
                  </button>)}
              </div>}
          </div>

          {/* Services Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-251">
            <button onClick={() => setServicesOpen(!servicesOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isActive('/services') ? 'text-primary' : 'text-dark'}`} data-id="element-252">
              Services
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-253" />
            </button>
            {servicesOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-254">
                {services.map((service, idx) => <button key={idx} onClick={() => handleNavClick(service.path)} className="text-left text-dark-muted" data-id="element-255">
                    {service.label}
                  </button>)}
              </div>}
          </div>

          <button onClick={() => handleNavClick('/resources')} className={`text-left text-lg font-medium py-2 border-b border-gray-100 ${isActive('/resources') ? 'text-primary' : 'text-dark'}`} data-id="element-256">
            Resources
          </button>

          {/* Careers Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-257">
            <button onClick={() => setCareersOpen(!careersOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isCareersActive ? 'text-primary' : 'text-dark'}`} data-id="element-258">
              Careers
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${careersOpen ? 'rotate-180' : ''}`} data-id="element-259" />
            </button>
            {careersOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-260">
                {careersItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className="text-left text-dark-muted" data-id="element-261">
                    {item.label}
                  </button>)}
              </div>}
          </div>

          <button onClick={() => handleNavClick('/contact')} className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center shadow-md" data-id="element-262">
            Contact Us Today
          </button>
        </div>
      </div>
    </header>;
}